<template>
  <v-card>
    <v-card-text>
      <div class="d-flex justify-space-between align-center ml-4 mt-3">
        <div class="d-flex flex-column">
          <span class="subtitle1" v-text="$t('network.secureWebGateway.customDomainDns.title')" />
          <span
            class="body2 text-indigo-medium"
            v-text="$t('network.secureWebGateway.customDomainDns.description')"
          />
        </div>
      </div>
      <v-expansion-panels flat hide-actions v-model="expansionPanelState" :disabled="loading">
        <v-expansion-panel eager>
          <template #title>
            <div>
              <v-icon
                icon="$chevron"
                color="primary"
                :class="['text--semibold mr-2', { rotated: expansionPanelState === 0 }]"
              />
              <span class="subtitle1">
                {{
                  $t("network.secureWebGateway.customDomainDns.expansionPanelLabel", { n: total })
                }}
              </span>
            </div>
            <div class="d-flex align-center" @click.stop>
              <cui-filter-wrapper
                class="mr-2 flex-row-reverse"
                :show-clear-button="showClearFiltersButton"
                @clear-filters-clicked="clearFilters()"
              >
                <v-text-field
                  v-model.trim="localFilters.search"
                  data-testid="users-page-users-search"
                  :class="{ 'filter-active': localFilters.search?.length }"
                  bg-color="indigo-faint"
                  variant="solo"
                  density="compact"
                  rounded="pill"
                  :placeholder="$t('general.search')"
                  :aria-label="$t('general.search')"
                  :disabled="loading"
                  class="search-field ml-2"
                  prepend-inner-icon="icon-search-xs"
                  clearable
                  clear-icon="icon-close-xs"
                  hide-details
                />
              </cui-filter-wrapper>
              <v-btn
                class="add-btn"
                color="primary"
                rounded
                large
                :disabled="loading"
                @click="addDnsRecord()"
              >
                <v-icon icon="$add" color="white" />
                <span v-text="$t('general.add')" />
              </v-btn>
            </div>
          </template>
          <v-expansion-panel-text>
            <custom-domain-dns-table
              :total-items="total"
              :items="items"
              :loading="loading"
              :filters-update-time="filtersChangeTimestamp"
              @action-applied="onAction($event)"
              @page-changed="onPageChanged"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { storeToRefs } from "pinia";
import CustomDomainDnsTable from "@/components/tables/CustomDomainDnsTable.vue";
import {
  type BaseCustomDomainDnsItem,
  type CustomDomainDnsItem,
  useCustomDomainDnsStore,
} from "@/_store/network/swg/custom-domain-dns.module.ts";
import { FilterContext } from "@/_store/filters.module.ts";
import { CuiFilterWrapper } from "@coronet/cui";
import { useFilters } from "@/composables/useFilters.ts";
import { CustomDomainDnsAction } from "@/constants/network.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import AddCustomDomainDnsModal from "@/components/modals/network/AddCustomDomainDnsModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import RemoveCustomDomainDnsModal from "@/components/modals/network/RemoveCustomDomainDnsModal.vue";
import isEqual from "lodash/isEqual";
import type { Pagination } from "@/types.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CuiFilterWrapper,
    CustomDomainDnsTable,
  },
  setup() {
    const customDomainDnsStore = useCustomDomainDnsStore();
    const dialogsStore = useDialogsStore();
    const { items, total } = storeToRefs(customDomainDnsStore);
    const { getItems, deleteItems, createItem } = customDomainDnsStore;
    const expansionPanelState = ref(0);
    const filtersChangeTimestamp = ref(0);

    const { clearFilters, showClearFiltersButton, localFilters } = useFilters({
      context: FilterContext.CUSTOM_DOMAIN_DNS,
      searchKey: "search",
      onFilterChange: async () => {
        await getItems();
      },
    });

    const onAction = async (payload: {
      action: CustomDomainDnsAction;
      selectedItems: CustomDomainDnsItem[];
    }) => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: CustomDomainDnsAction.REMOVE,
          component: RemoveCustomDomainDnsModal,
          width: ModalWidth.SMALL,
          item: payload.selectedItems,
          callback: async () => {
            const itemsToDelete = payload.selectedItems.map((i) => i.id);
            await deleteItems(itemsToDelete);
            filtersChangeTimestamp.value = new Date().getTime();
          },
        }),
      });
    };

    const addDnsRecord = async () => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: CustomDomainDnsAction.ADD,
          component: AddCustomDomainDnsModal,
          width: ModalWidth.MEDIUM,
          callback: async (item: BaseCustomDomainDnsItem) => {
            createItem(item);
            filtersChangeTimestamp.value = new Date().getTime();
          },
        }),
      });
    };

    const onPageChanged = async (pagination: Pagination): Promise<void> => {
      if (!isEqual(pagination, customDomainDnsStore.pagination)) {
        customDomainDnsStore.$patch({
          pagination,
        });
        await getItems();
      }
    };

    onMounted(async () => {
      await getItems();
    });

    return {
      items,
      total,
      expansionPanelState,
      clearFilters,
      showClearFiltersButton,
      localFilters,
      getItems,
      onAction,
      addDnsRecord,
      onPageChanged,
      filtersChangeTimestamp,
    };
  },
});
</script>

<style scoped lang="scss">
.search-field {
  :deep(.v-field) {
    border-radius: 18px;
    padding: 6px;
    min-height: 36px;
    box-shadow: none;
  }

  :deep(.v-field__input) {
    padding: 0;
    min-height: 24px;
  }

  :deep(.v-field__prepend-inner) {
    padding-right: 8px;
    .icon-search-xs:before {
      color: var(--v-theme-indigo-medium);
    }
  }

  :deep(.v-field__append-inner) {
    padding-left: 8px;
  }
}

:deep(*) {
  .search-field {
    width: 201px;
  }
}
</style>
